import { useState, useCallback, useMemo, useEffect } from "react";
import { DoorHanding } from "../../../entities-generated/common/door-handing";
import { IDoorHandingOption } from "../../../entities-generated/common/door-handing-option";
import { DoorSize } from "../../../entities-generated/common/door-size";
import { IDoorSizeOption } from "../../../entities-generated/common/door-size-option";
import { DoorType } from "../../../entities-generated/common/door-type";
import { IDoorTypeOption } from "../../../entities-generated/common/door-type-option";
import { ILaborCallout, LaborCallout } from "../../../entities-generated/common/labor-callout";
import { BwjInput } from "../../../framework/entities/bwj-input";
import { BwjValidityState } from "../../../framework/entities/bwj-validity-state";
import { EventNotificationService, useEventNotification } from "../../../framework/services/event-notification";
import { ImmutableFormValueService, ValidatedFormValueService, MutableFormValueService, IFormValidityCheck } from "../../../framework/services/form-model-service-components";
import { NavigationService } from "../../../framework/services/navigation";
import { contentChanged } from "../../../framework/utils";
import { firstOrDefault, forEach, groupBy, none, sum, where } from "../../../framework/utils/collection";
import { IAssessmentDoorEditingModel, AssessmentDoorEditingModel } from "../entities/assess-rec-door-fm";
import { ProjectEvents } from "../events";
import { IAssessmentRecorderDoorFormModel, IAssessmentRecorderDoorValueModel, IAssessmentRecorderDoorModelChange } from "./assess-record-door-iface";
import { IBrowserImage } from "../../../framework/components/bwj-image-browser";
import { ImageApiClient } from "../../../services/image";
import { IDoorAssessment } from "../../../entities-generated/common/door-assessment";
import { IMaterialCallout } from "../../../entities-generated/common/material-callout";

// ##BEGIN:DEVELOPERCODE##

// this is a good place for doing things like defining custom form value service types

// ##END:DEVELOPERCODE##

// %%BEGIN:id/immutable/string%%
export const id = new (class idService extends ImmutableFormValueService<IAssessmentDoorEditingModel, string> {
    public initialize(data: IAssessmentDoorEditingModel): void {
        this.value = data.id;
    }

    public applyToDataModel(data: IAssessmentDoorEditingModel): void {
        data.id = this.value;
    }

    public getDefaultValue(): string {
        return '';
    }

    public getInputValue(): string {
        return this.value;
    }

})(
    'id' // id
    ,'' // description
);
// %%END:id/immutable/string%%

// %%BEGIN:doorType/validated/IDoorTypeOption | null/DoorType%%
export const doorType = new (class doorTypeService extends ValidatedFormValueService<IAssessmentDoorEditingModel, IDoorTypeOption | null, DoorType> {
    public initialize(data: IAssessmentDoorEditingModel): void {
        this.options = data.doorTypeOptions;
        const dt = firstOrDefault(this.options, d => d.id === data.doorType);
        this.setInputValue(new BwjInput<IDoorTypeOption | null>(dt));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentDoorEditingModel): void {
        data.doorTypeOptions = this.options;
        data.doorType = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<IDoorTypeOption | null> {
        return new BwjInput<IDoorTypeOption | null>(null);
    }

    public getInputValue(): BwjInput<IDoorTypeOption | null> {
        return this.value;
    }

    public setInputValue(value: BwjInput<IDoorTypeOption | null>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): DoorType {
        const value = this.value.value;
        return value?.id ?? DoorType.Indeterminate;
    }

    public validate(value: IDoorTypeOption | null): Promise<BwjInput<IDoorTypeOption | null>> {
        const result = this.requiredFieldValidator(value, 'Select a door type');
        return Promise.resolve(result);
    }

    public onUpdate(value: IDoorTypeOption | null): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!value && size.setOptions(value.id);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    public options: IDoorTypeOption[] = [];
})(
    'doorType' // id
    ,'Door type' // description
);
// %%END:doorType/validated/IDoorTypeOption | null/DoorType%%

// %%BEGIN:handing/validated/IDoorHandingOption | null/DoorHanding%%
export const handing = new (class handingService extends ValidatedFormValueService<IAssessmentDoorEditingModel, IDoorHandingOption | null, DoorHanding> {
    public initialize(data: IAssessmentDoorEditingModel): void {
        this.options = data.doorHandingOptions;
        const dh = firstOrDefault(this.options, h => h.id === data.handing);
        this.setInputValue(new BwjInput<IDoorHandingOption | null>(dh));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentDoorEditingModel): void {
        data.doorHandingOptions = this.options;
        data.handing = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<IDoorHandingOption | null> {
        return new BwjInput<IDoorHandingOption | null>(null);
    }

    public getInputValue(): BwjInput<IDoorHandingOption | null> {
        return this.value;
    }

    public setInputValue(value: BwjInput<IDoorHandingOption | null>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): DoorHanding {
        const value = this.value.value;
        return value?.id ?? DoorHanding.Unspecified;
    }

    public validate(value: IDoorHandingOption | null): Promise<BwjInput<IDoorHandingOption | null>> {
        const result = this.requiredFieldValidator(value, 'Select a handing option');
        return Promise.resolve(result);
    }

    public onUpdate(value: IDoorHandingOption | null): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    public options: IDoorHandingOption[] = [];
})(
    'handing' // id
    ,'Door handing' // description
);
// %%END:handing/validated/IDoorHandingOption | null/DoorHanding%%

// %%BEGIN:size/validated/IDoorSizeOption | null/DoorSize%%
export const size = new (class sizeService extends ValidatedFormValueService<IAssessmentDoorEditingModel, IDoorSizeOption | null, DoorSize> {
    public initialize(data: IAssessmentDoorEditingModel): void {
        this.allSizes = data.doorSizeOptions;
        this.setOptions(data.doorType);
        const ds = firstOrDefault(this.allSizes, s => s.id === data.size);
        this.setInputValue(new BwjInput<IDoorSizeOption | null>(ds));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentDoorEditingModel): void {
        data.doorSizeOptions = this.allSizes;
        data.size = this.getOutputValue();
    }

    public getDefaultValue(): BwjInput<IDoorSizeOption | null> {
        return new BwjInput<IDoorSizeOption | null>(null);
    }

    public getInputValue(): BwjInput<IDoorSizeOption | null> {
        return this.value;
    }

    public setInputValue(value: BwjInput<IDoorSizeOption | null>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): DoorSize {
        const value = this.value.value;
        return value?.id ?? DoorSize.Indeterminate;
    }

    public validate(value: IDoorSizeOption | null): Promise<BwjInput<IDoorSizeOption | null>> {
        const result = this.requiredFieldValidator(value, 'Select a door size');
        return Promise.resolve(result);
    }

    public onUpdate(value: IDoorSizeOption | null): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

    public setOptions(doorType: DoorType): void {
        switch(doorType) {
            case DoorType.StormDoor:
                const stormSizes = where(this.allSizes, s => s.description.indexOf('Storm:') > -1)
                .map(s => {
                    const option: IDoorSizeOption = {
                        id: s.id,
                        description: s.description.replace('Storm: ', '')
                    };
                    return option;
                });
                this.options = stormSizes;
                break;
            default:
                this.options = [];
                break;
        }
    }

    public allSizes: IDoorSizeOption[] = [];
    public options: IDoorSizeOption[] = [];
})(
    'size' // id
    ,'Door size' // description
);
// %%END:size/validated/IDoorSizeOption | null/DoorSize%%

// %%BEGIN:cutdownRequired/mutable/boolean/boolean%%
export const cutdownRequired = new (class cutdownRequiredService extends MutableFormValueService<IAssessmentDoorEditingModel, boolean, boolean> {
    public initialize(data: IAssessmentDoorEditingModel): void {
        this.value = data.cutdownRequired;
    }

    public applyToDataModel(data: IAssessmentDoorEditingModel): void {
        data.cutdownRequired = this.value;
    }

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'cutdownRequired' // id
    ,'Door height must be cut to fit' // description
);
// %%END:cutdownRequired/mutable/boolean/boolean%%

// %%BEGIN:existingDoorPresent/mutable/boolean/boolean%%
export const existingDoorPresent = new (class existingDoorPresentService extends MutableFormValueService<IAssessmentDoorEditingModel, boolean, boolean> {
    public initialize(data: IAssessmentDoorEditingModel): void {
        this.value = data.existingDoorPresent;
    }

    public applyToDataModel(data: IAssessmentDoorEditingModel): void {
        data.existingDoorPresent = this.value;
    }

    public getDefaultValue(): boolean {
        return false;
    }

    public getInputValue(): boolean {
        return this.value;
    }

    public setInputValue(value: boolean): void {
        this.value = value;
    }

    public getOutputValue(): boolean {
        return this.value;
    }

    public onUpdate(value: boolean): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'existingDoorPresent' // id
    ,'Existing door of this type present' // description
);
// %%END:existingDoorPresent/mutable/boolean/boolean%%

// %%BEGIN:alias/validated/string/string%%
export const alias = new (class aliasService extends ValidatedFormValueService<IAssessmentDoorEditingModel, string, string> {
    public initialize(data: IAssessmentDoorEditingModel): void {
        this.setInputValue(new BwjInput<string>(data.alias));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentDoorEditingModel): void {
        data.alias = this.value.value;
    }

    public getDefaultValue(): BwjInput<string> {
        return new BwjInput<string>('');
    }

    public getInputValue(): BwjInput<string> {
        return this.value;
    }

    public setInputValue(value: BwjInput<string>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): string {
        return this.value.value;
    }

    public validate(value: string): Promise<BwjInput<string>> {
        const result = this.requiredFieldValidator(value, 'Enter entryway location');
        return Promise.resolve(result);
    }

    public onUpdate(value: string): void {
        this.validate(value).then(result => {
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id); });
        this.valueUntouched = false;
    }

})(
    'alias' // id
    ,'Entryway location' // description
);
// %%END:alias/validated/string/string%%

// %%BEGIN:photoIds/mutable/IBrowserImage[]/string[]%%
export const photoIds = new (class photoIdsService extends MutableFormValueService<IAssessmentDoorEditingModel, IBrowserImage[], string[]> {
    public initialize(data: IAssessmentDoorEditingModel): void {
        this.value = data.photoIds.map(src => { return { src }; });
    }

    public applyToDataModel(data: IAssessmentDoorEditingModel): void {
        data.photoIds = this.getOutputValue();
    }

    public getDefaultValue(): IBrowserImage[] {
        return [];
    }

    public getInputValue(): IBrowserImage[] {
        return this.value;
    }

    public setInputValue(value: IBrowserImage[]): void {
        this.value = value;
    }

    public getOutputValue(): string[] {
        const value = this.value.map(img => img.src);
        return value;
    }

    public onUpdate(value: IBrowserImage[]): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'photoIds' // id
    ,'' // description
);
// %%END:photoIds/mutable/IBrowserImage[]/string[]%%

// %%BEGIN:photoIdsUpload/validated/File[]/File[]%%
export const photoIdsUpload = new (class photoIdsUploadService extends ValidatedFormValueService<IAssessmentDoorEditingModel, File[], File[]> {
    public initialize(data: IAssessmentDoorEditingModel): void {
        this.setInputValue(new BwjInput<File[]>([]));
        this.valueUntouched = true;
    }

    public applyToDataModel(data: IAssessmentDoorEditingModel): void {}

    public getDefaultValue(): BwjInput<File[]> {
        return new BwjInput<File[]>([]);
    }

    public getInputValue(): BwjInput<File[]> {
        return this.value;
    }

    public setInputValue(value: BwjInput<File[]>): void {
        value.id = this.id;
        value.description = this.description;
        this.value = value;
    }

    public getOutputValue(): File[] {
        return this.value.value;
    }

    public validate(value: File[]): Promise<BwjInput<File[]>> {
        const result = this.getInputObject(value);
        return Promise.resolve(result);
    }

    public onUpdate(value: File[]): void {
        const pendingResult = new BwjInput<File[]>(value);
        pendingResult.id = this.id;
        pendingResult.description = this.description;
        pendingResult.validity = BwjValidityState.Indeterminate;
        pendingResult.message = 'One moment please...';
        this.setInputValue(pendingResult);
        !!this.publishChange && this.publishChange(this.id);

        ImageApiClient.uploadImage(value)
        .then(resp => {
            if(!resp) { return; }
            const nuevos = resp.imageUrls.map(src => { return { src }; });
            photoIds.setInputValue([...photoIds.getInputValue(), ...nuevos]);

            const result = this.getDefaultValue();
            this.setInputValue(result);
            !!this.publishChange && this.publishChange(this.id);
        })
        .catch(() => {
            const verr = this.getDefaultValue();
            verr.validity = BwjValidityState.ValidationError;
            verr.message = 'Image upload failed';
            this.setInputValue(verr);
            !!this.publishChange && this.publishChange(this.id);
        })
    }

})(
    'photoIdsUpload' // id
    ,'Related photos' // description
);
// %%END:photoIdsUpload/validated/File[]/File[]%%

// %%BEGIN:laborScope/mutable/LaborCallout[]/LaborCallout[]%%
export const laborScope = new (class laborScopeService extends MutableFormValueService<IAssessmentDoorEditingModel, LaborCallout[], LaborCallout[]> {
    public initialize(data: IAssessmentDoorEditingModel): void {
        this.value = data.laborScope;
    }

    public applyToDataModel(data: IAssessmentDoorEditingModel): void {
        data.laborScope = this.value;
    }

    public getDefaultValue(): LaborCallout[] {
        return [];
    }

    public getInputValue(): LaborCallout[] {
        return this.value;
    }

    public setInputValue(value: LaborCallout[]): void {
        this.value = value;
    }

    public getOutputValue(): LaborCallout[] {
        return this.value;
    }

    public onUpdate(value: LaborCallout[]): void {
        this.value = value;
        !!this.publishChange && this.publishChange(this.id);
    }
})(
    'laborScope' // id
    ,'Labor' // description
);
// %%END:laborScope/mutable/LaborCallout[]/LaborCallout[]%%

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toFormModel = (): IAssessmentRecorderDoorFormModel => {
    return {
       doorType: doorType.getInputValue(),
       handing: handing.getInputValue(),
       size: size.getInputValue(),
       cutdownRequired: cutdownRequired.getInputValue(),
       existingDoorPresent: existingDoorPresent.getInputValue(),
       alias: alias.getInputValue(),
       photoIds: photoIds.getInputValue(),
       photoIdsUpload: photoIdsUpload.getInputValue(),
       laborScope: laborScope.getInputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const toValueModel = (): IAssessmentRecorderDoorValueModel => {
    return {
       id: id.getOutputValue(),
       doorType: doorType.getOutputValue(),
       handing: handing.getOutputValue(),
       size: size.getOutputValue(),
       cutdownRequired: cutdownRequired.getOutputValue(),
       existingDoorPresent: existingDoorPresent.getOutputValue(),
       alias: alias.getOutputValue(),
       photoIds: photoIds.getOutputValue(),
       photoIdsUpload: photoIdsUpload.getOutputValue(),
       laborScope: laborScope.getOutputValue(),
    };
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const getValidatedInputs = () => {
    return [
        doorType.getInputValue(),
        handing.getInputValue(),
        size.getInputValue(),
        alias.getInputValue(),
        photoIdsUpload.getInputValue(),
    ];
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isFormValid = () => none(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const getFormErrors = () => where(getValidatedInputs(), (i) => i.validity === BwjValidityState.Invalid);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const broadcastChange = (propertyName: string) => {
    const arg: IAssessmentRecorderDoorModelChange = {
        propertyChanged: propertyName,
        model: toFormModel()
    };

    EventNotificationService.dispatch(ProjectEvents.AssessRecordDoorModelDidUpdate, arg);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const validateAndUpdateModel = (): Promise<IFormValidityCheck<IAssessmentRecorderDoorFormModel>> => {
    const promises = [
        doorType.formValidateAndUpdate(),
        handing.formValidateAndUpdate(),
        size.formValidateAndUpdate(),
        alias.formValidateAndUpdate(),
        photoIdsUpload.formValidateAndUpdate(),
    ];

    return Promise.all(promises)
        .then(() => { 
            broadcastChange('validateAndUpdateModel:IAssessmentRecorderDoorFormModel');
            return { isValid: isFormValid(), model: toFormModel() };
        });
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const isModelChanged = () => {
    if(initialState.valueModel === undefined) { return false; }
    const current = toValueModel();
    return contentChanged(initialState.valueModel, current);
}

// WARNING: GENERATED CODE -- changes will be lost on regeneration
const initialState: { valueModel?: IAssessmentRecorderDoorValueModel, dataModel: IAssessmentDoorEditingModel } = {
    dataModel: new AssessmentDoorEditingModel()
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const initializeService = (data?: IAssessmentDoorEditingModel) => {
    const dataModel = data || new AssessmentDoorEditingModel();

    id.initialize(dataModel);
    doorType.initialize(dataModel);
    handing.initialize(dataModel);
    size.initialize(dataModel);
    cutdownRequired.initialize(dataModel);
    existingDoorPresent.initialize(dataModel);
    alias.initialize(dataModel);
    photoIds.initialize(dataModel);
    photoIdsUpload.initialize(dataModel);
    laborScope.initialize(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
    broadcastChange('initializeService:IAssessmentRecorderDoorFormModel');
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
doorType.setChangePublisher(broadcastChange);
handing.setChangePublisher(broadcastChange);
size.setChangePublisher(broadcastChange);
cutdownRequired.setChangePublisher(broadcastChange);
existingDoorPresent.setChangePublisher(broadcastChange);
alias.setChangePublisher(broadcastChange);
photoIds.setChangePublisher(broadcastChange);
photoIdsUpload.setChangePublisher(broadcastChange);
laborScope.setChangePublisher(broadcastChange);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const setReversionState = () => {
    const dataModel = new AssessmentDoorEditingModel();

    id.applyToDataModel(dataModel);
    doorType.applyToDataModel(dataModel);
    handing.applyToDataModel(dataModel);
    size.applyToDataModel(dataModel);
    cutdownRequired.applyToDataModel(dataModel);
    existingDoorPresent.applyToDataModel(dataModel);
    alias.applyToDataModel(dataModel);
    photoIds.applyToDataModel(dataModel);
    photoIdsUpload.applyToDataModel(dataModel);
    laborScope.applyToDataModel(dataModel);

    initialState.valueModel = toValueModel();
    initialState.dataModel = dataModel;
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const revertChanges = () => {
    initializeService(initialState.dataModel);
};

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModelChangeEvent = (onChangeWrappedInUseCallback: ((change: IAssessmentRecorderDoorModelChange) => void), subscriberId: string) =>
    useEventNotification(onChangeWrappedInUseCallback, [onChangeWrappedInUseCallback], ProjectEvents.AssessRecordDoorModelDidUpdate, subscriberId);

// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useFormModel = (subscriberId: string): IAssessmentRecorderDoorFormModel => {
    const [getter, setter] = useState<IAssessmentRecorderDoorFormModel>(toFormModel());

    useFormModelChangeEvent(useCallback((change: IAssessmentRecorderDoorModelChange) => setter(change.model), []), subscriberId);

    return getter;
}
// WARNING: GENERATED CODE -- changes will be lost on regeneration
export const useHasUnsavedChanges = (model: IAssessmentRecorderDoorFormModel, warnOnUnsavedPageNavigation: boolean = false) => {
    const hasUnsavedChanges = useMemo(isModelChanged, [model]);

    useEffect(() => {
        if(warnOnUnsavedPageNavigation === false || hasUnsavedChanges === false) { return; }

        const unloadCallback = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
            return '';
        };

        window.addEventListener('beforeunload', unloadCallback);
        NavigationService.registerObjection('AssessmentRecorderDoorFormService');

        return () => {
            window.removeEventListener('beforeunload', unloadCallback);
            NavigationService.rescindObjection('AssessmentRecorderDoorFormService');
        }
    }, [warnOnUnsavedPageNavigation, hasUnsavedChanges]);

    return hasUnsavedChanges;
}
// ##BEGIN:DEVELOPERCODE##

const consolidateMaterials = (labor: ILaborCallout[]) => {
    const matList: IMaterialCallout[] = [];
    const matLists = labor.map(l => l.materials);
    forEach(matLists, ml => {
        ml.map(m => matList.push(m));
    });

    const result: IMaterialCallout[] = [];
    const matMap = groupBy(matList, m => m.standardMaterialId ?? m.vendorUrl);
    forEach(matMap, group => {
        const material = {...group[0]};
        material.quantity = sum(group, m => m.quantity);
        result.push(material);
    });

    return result;
}

export const toDoorAssessment = () => {
    const v = toValueModel();
    const request: IDoorAssessment = {
        id: v.id,
        doorType: v.doorType,
        handing: v.handing,
        size: v.size,
        cutdownRequired: v.cutdownRequired,
        alias: v.alias,
        photoIds: v.photoIds,
        laborScope: v.laborScope,
        materials: consolidateMaterials(v.laborScope),
        existingDoorPresent: v.existingDoorPresent
    };
    return request;
};

// ##END:DEVELOPERCODE##
